import moment from "moment-timezone";
import { DEFAULT_TIMEZONE } from "@/const.js";
import { i18n } from "@/main";

function debounce(func, delay) {
  let timeoutId;

  return function(...args) {
    const context = this;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
}

function throttle(func, delay) {
  let lastExecutionTime = 0;
  let timeoutId;

  return function(...args) {
    const context = this;
    const currentTime = Date.now();

    if (currentTime - lastExecutionTime >= delay) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      func.apply(context, args);
      lastExecutionTime = currentTime;
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        func.apply(context, args);
        lastExecutionTime = Date.now();
        timeoutId = null;
      }, delay - (currentTime - lastExecutionTime));
    }
  };
}

function coordinatesGeocoder(query) {
  // match anything which looks like a decimal degrees coordinate pair
  var matches = query.match(
    /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
  );
  if (!matches) {
    return null;
  }

  function coordinateFeature(lng, lat) {
    return {
      center: [lng, lat],
      geometry: {
        type: "Point",
        coordinates: [lng, lat]
      },
      place_name: "Lat: " + lat + " Lng: " + lng,
      place_type: ["coordinate"],
      properties: {},
      type: "Feature"
    };
  }

  var coord1 = Number(matches[1]);
  var coord2 = Number(matches[2]);
  var geocodes = [];

  if (coord1 < -90 || coord1 > 90) {
    // must be lng, lat
    geocodes.push(coordinateFeature(coord1, coord2));
  }

  if (coord2 < -90 || coord2 > 90) {
    // must be lat, lng
    geocodes.push(coordinateFeature(coord2, coord1));
  }

  if (geocodes.length === 0) {
    // else could be either lng, lat or lat, lng
    geocodes.push(coordinateFeature(coord1, coord2));
    geocodes.push(coordinateFeature(coord2, coord1));
  }

  return geocodes;
}

function timeZeroPadding(num) {
  return num < 10 ? "0" + num : "" + num;
}

function hoursAndMinutesToSeconds(timeStr) {
  return timeStr.split(":").reduce((accumulator, current, idx) => {
    current = +current;
    if (idx === 0) {
      return accumulator + current * 60 * 60;
    }
    if (idx === 1) {
      return accumulator + current * 60;
    }
  }, 0);
}

function secondsToHoursAndMinutes(seconds, formatToString = false) {
  const duration = moment.duration(seconds * 1000);

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  if (formatToString) {
    return `${timeZeroPadding(hours)}:${timeZeroPadding(minutes)}`;
  }

  return { hours: hours, minutes: minutes };
}

function generateFutureEventsInRaw({
  daysOfWeek,
  startTime,
  endTime,
  startDate,
  endDate,
  timezone = DEFAULT_TIMEZONE,
  duration
}) {
  // const daysOfWeek = [1: "Monday", 2: "Tuesday", 3: "Wednesday", 7: "Sunday"];
  daysOfWeek = daysOfWeek.map(item => {
    if (item === "7") {
      item = "0";
    }
    return item;
  });
  const events = [];

  const futureEndDate = moment(endDate)
    .tz(timezone)
    .endOf("day");
  let _start = moment(startDate).tz(timezone),
    _now = moment().tz(timezone);
  let iter = _start < _now ? _now : _start;
  while (iter <= futureEndDate) {
    if (
      daysOfWeek.includes(
        iter.day() + "" // get weekday
      )
    ) {
      let current = moment(iter)
        .tz(timezone)
        .startOf("day")
        .set({
          hour: startTime.hours,
          minute: startTime.minutes
        });
      const end = moment(iter)
        .tz(timezone)
        .startOf("day")
        .set({
          hour: endTime.hours,
          minute: endTime.minutes
        });
      while (current.isBefore(end)) {
        let nextHour = moment(current)
          .tz(timezone)
          .add(duration, "minutes");

        const startDateTime = moment(current);
        const endDateTime = nextHour;
        current.add(duration, "minutes");

        // must make a reservation 24 hours in advance
        const twentyFourHoursFromNow = moment()
          .tz(timezone)
          .add(24, "hours");
        if (startDateTime.isBefore(twentyFourHoursFromNow)) {
          continue;
        }

        if (endDateTime.isAfter(end)) {
          break;
        }
        events.push({
          title: i18n.t("Available"),
          start: moment(startDateTime)
            .tz(timezone)
            .toDate(),
          end: moment(endDateTime)
            .tz(timezone)
            .toDate()
        });
      }
    }

    iter.add(1, "day");
  }
  return events;
}

function generateEvents({
  daysOfWeek,
  startTime,
  endTime,
  startDate,
  endDate,
  events,
  timezone = DEFAULT_TIMEZONE,
  duration = 60
}) {
  const futureEventsInRaw = generateFutureEventsInRaw({
    daysOfWeek,
    startTime,
    endTime,
    startDate,
    endDate,
    timezone,
    duration
  });
  let futureAvailable = [],
    pastBook = [],
    futureBook = [];

  // get pastEvents
  let now = moment()
    .tz(timezone)
    .valueOf();
  for (let event = 0; event < events.length; event++) {
    const eventDate = events[event];
    const bookingStart = eventDate.booking_start_time.$date || 0;
    const bookingEnd = eventDate.booking_end_time.$date || 0;

    if (bookingStart < now) {
      // past booking
      pastBook.push({
        title: eventDate.package_title,
        start: moment(bookingStart)
          .tz(timezone)
          .toDate(),
        end: moment(bookingEnd)
          .tz(timezone)
          .toDate(),
        classNames: ["booked-past"],
        eventInfo: eventDate
      });
    } else {
      // future booking
      futureBook.push({
        title: eventDate.package_title,
        start: moment(bookingStart)
          .tz(timezone)
          .toDate(),
        end: moment(bookingEnd)
          .tz(timezone)
          .toDate(),
        classNames: ["booked-future"],
        eventInfo: eventDate
      });
    }
  }

  for (let future = 0; future < futureEventsInRaw.length; future++) {
    const futuretDate = futureEventsInRaw[future];
    const futureStart = +futuretDate.start;
    const futureEnd = +futuretDate.end;

    if (isContain(futureStart, futureEnd, events)) {
      continue;
    } else {
      futuretDate.classNames = ["available-event"];
      futureAvailable.push(futuretDate);
    }
  }
  return { futureAvailable, pastBook, futureBook };
}

function isContain(futureStart, futureEnd, events) {
  for (let event = 0; event < events.length; event++) {
    const eventDate = events[event];

    const bookingStart = eventDate.booking_start_time.$date || 0;
    const bookingEnd = eventDate.booking_end_time.$date || 0;
    // if (futureStart >= bookingStart && futureStart < bookingEnd) {
    //   return true;
    // } else if (futureEnd > bookingStart && futureEnd < bookingEnd) {
    //   return true;
    // }

    if (is_overlapping(futureStart, futureEnd, bookingStart, bookingEnd)) {
      return true;
    }
  }
  return false;
}

function is_overlapping(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}

function taxRate(province) {
  let taxRates = 0,
    taxType = undefined;
  if (
    province === "AB" ||
    province === "NT" ||
    province === "NU" ||
    province === "YT"
  ) {
    taxRates = 0.05;
    taxType = "GST";
  } else if (province === "BC" || province === "MB") {
    taxRates = 0.12;
    taxType = "GST/PST";
  } else if (
    province === "NB" ||
    province === "NL" ||
    province === "NS" ||
    province === "PE"
  ) {
    taxRates = 0.15;
    taxType = "HST";
  } else if (province === "ON") {
    taxRates = 0.13;
    taxType = "HST";
  } else if (province === "QC") {
    taxRates = 0.14975;
    taxType = "GST/QST";
  } else if (province === "SK") {
    taxRates = 0.11;
    taxType = "GST/PST";
  }

  return { taxRates, taxType };
}

export {
  debounce,
  throttle,
  coordinatesGeocoder,
  timeZeroPadding,
  hoursAndMinutesToSeconds,
  secondsToHoursAndMinutes,
  generateFutureEventsInRaw,
  generateEvents,
  taxRate
};
